import { useEffect, useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { CheckCircle, XCircle } from 'lucide-react'

export default function Approve() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [message, setMessage] = useState('')

  useEffect(() => {
    if (!token) {
      setStatus('error')
      setMessage('Invalid approval link. No token provided.')
      return
    }
    const apiUrl = `/api/auth/approve/${token}`
    fetch(apiUrl)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}))
        if (res.ok && data.success) {
          setStatus('success')
          setMessage(data.message || 'Your account has been approved.')
        } else {
          setStatus('error')
          setMessage(data.detail || data.message || 'Invalid or expired approval link.')
        }
      })
      .catch((err) => {
        setStatus('error')
        setMessage(err?.message || 'Failed to approve account. The link may have expired.')
      })
  }, [token])

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-6">
      <div className="max-w-md w-full text-center space-y-6">
        {status === 'loading' && (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
            <p className="text-muted-foreground">Approving your account...</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="flex justify-center">
              <div className="rounded-full bg-green-500/20 p-4">
                <CheckCircle className="h-12 w-12 text-green-500" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-foreground">Account Approved</h1>
            <p className="text-muted-foreground">{message}</p>
            <Button asChild>
              <Link to="/login">Go to Login</Link>
            </Button>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="flex justify-center">
              <div className="rounded-full bg-destructive/20 p-4">
                <XCircle className="h-12 w-12 text-destructive" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-foreground">Approval Failed</h1>
            <p className="text-muted-foreground">{message}</p>
            <Button variant="outline" asChild>
              <Link to="/login">Go to Login</Link>
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
